import { useState } from 'react'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { useSizes } from '../../../features/products/hooks/useMetaData'
import { metaDataApi } from '../../../features/products/api/metaDataApi'
import { MetaTable } from '../components/MetaTable'
import { Input } from '../../../shared/components'
import Button from '../../../shared/components/Button'

export const SizesTab = () => {
  const { data, isLoading } = useSizes()
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)

  const createMutation = useMutation({
    mutationFn: ({ name, order }: { name: string; order: number }) => metaDataApi.createSize(name, order),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sizes'] }),
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => { await metaDataApi.updateSize(id, name) },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['sizes'] }); setEditingId(null) },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => metaDataApi.deleteSize(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sizes'] }),
  })

  const items = (data?.data ?? []) as unknown as Record<string, unknown>[]
  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'order', label: 'Order' },
  ]

  const renderAddForm = (onDone: () => void) => (
    <AddSizeForm
      isLoading={createMutation.isPending}
      onSubmit={async (name) => {
        const order = items.length + 1
        await createMutation.mutateAsync({ name, order })
        onDone()
      }}
      onCancel={onDone}
    />
  )

  const renderEditForm = (item: Record<string, unknown>, onDone: () => void) => (
    <EditSizeForm
      defaultValue={item.name as string}
      isLoading={updateMutation.isPending}
      onSubmit={async (name) => updateMutation.mutateAsync({ id: item.id as string, name })}
      onCancel={onDone}
    />
  )

  return (
    <MetaTable
      title="Sizes"
      items={items}
      columns={columns}
      isLoading={isLoading}
      addLabel="Add Size"
      renderAddForm={renderAddForm}
      onEdit={(item) => setEditingId(editingId === (item.id as string) ? null : (item.id as string))}
      onDelete={(item) => deleteMutation.mutate(item.id as string)}
      editingId={editingId}
      renderEditForm={renderEditForm}
    />
  )
}

const AddSizeForm = ({ isLoading, onSubmit, onCancel }: {
  isLoading: boolean
  onSubmit: (name: string) => Promise<void>
  onCancel: () => void
}) => {
  const [value, setValue] = useState('')
  return (
    <div className="flex items-end gap-3">
      <div className="flex-1">
        <Input label="Size name" placeholder="e.g. XL, 4XL" value={value} onChange={e => setValue(e.target.value)} className="h-[38px]" />
      </div>
      <Button size="sm" onClick={() => onSubmit(value)} disabled={!value.trim() || isLoading} loading={isLoading} className="h-[38px]">Save</Button>
      <Button size="sm" variant="outline" onClick={onCancel} className="h-[38px]">Cancel</Button>
    </div>
  )
}

const EditSizeForm = ({ defaultValue, isLoading, onSubmit, onCancel }: {
  defaultValue: string
  isLoading: boolean
  onSubmit: (name: string) => Promise<void>
  onCancel: () => void
}) => {
  const [value, setValue] = useState(defaultValue)
  return (
    <div className="flex items-end gap-3">
      <div className="flex-1">
        <Input label="Size name" value={value} onChange={e => setValue(e.target.value)} className="h-[38px]" />
      </div>
      <Button size="sm" onClick={() => onSubmit(value)} disabled={!value.trim() || isLoading} loading={isLoading} className="h-[38px]">Update</Button>
      <Button size="sm" variant="outline" onClick={onCancel} className="h-[38px]">Cancel</Button>
    </div>
  )
}
